import { useEffect, useRef, useState } from 'react';
import { Button, Card, Input, Spin, theme } from '../ui';
import { Bot, X, Send } from 'lucide-react';
import { request } from '../api/client';
import { usePermission } from '../store/auth';

interface Msg { role: 'user' | 'assistant'; content: string }

/**
 * Floating AI assistant ("Krishi Mitra"). A FAB bottom-right opens a chat panel
 * that talks to POST /ai/chat (gated by AI.CHATBOT) and keeps the session.
 */
export function ChatbotWidget() {
  const { token } = theme.useToken();
  const canChat = usePermission('AI.CHATBOT'); // reactive: shows/hides live as the grant changes
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading, open]);

  if (!canChat) return null;

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setLoading(true);
    try {
      const r = await request<any>({ url: '/ai/chat', method: 'POST', data: { message: text, sessionId } });
      if (r?.sessionId) setSessionId(r.sessionId);
      setMessages((m) => [...m, { role: 'assistant', content: r?.reply || '…' }]);
    } catch {
      setMessages((m) => [...m, { role: 'assistant', content: 'Sorry, the assistant is unavailable right now.' }]);
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return (
      <Button
        variant="primary"
        aria-label="Open AI assistant"
        icon={<Bot size={22} />}
        onClick={() => setOpen(true)}
        style={{
          position: 'fixed', right: 24, bottom: 24, width: 56, height: 56,
          zIndex: 1000, boxShadow: '0 6px 18px rgba(0,0,0,0.28)', borderRadius: '50%',
        }}
      />
    );
  }

  return (
    <Card
      title={<span><Bot size={16} style={{ color: token.colorPrimary, marginRight: 8 }} />Krishi Mitra · AI Assistant</span>}
      extra={<Button variant="text" icon={<X size={16} />} onClick={() => setOpen(false)} aria-label="Close" />}
      style={{
        position: 'fixed', right: 24, bottom: 24, width: 370, maxWidth: 'calc(100vw - 32px)',
        height: 520, maxHeight: 'calc(100vh - 120px)', zIndex: 1000,
        display: 'flex', flexDirection: 'column', boxShadow: '0 12px 36px rgba(0,0,0,0.3)',
      }}
      bodyClassName="flex-1 flex flex-col p-0! overflow-hidden"
    >
      <div ref={bodyRef} style={{ flex: 1, overflowY: 'auto', padding: 12, background: token.colorFillQuaternary }}>
        {messages.length === 0 && (
          <div style={{ color: token.colorTextTertiary, fontSize: 13, textAlign: 'center', marginTop: 24 }}>
            Hi! Ask me about crops, products, dosage, or best practices.
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
            <div style={{
              maxWidth: '82%', padding: '7px 11px', borderRadius: 12, fontSize: 13, lineHeight: 1.45, whiteSpace: 'pre-wrap',
              background: m.role === 'user' ? token.colorPrimary : token.colorBgContainer,
              color: m.role === 'user' ? '#fff' : token.colorText,
              border: m.role === 'user' ? 'none' : `1px solid ${token.colorBorderSecondary}`,
            }}>
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ color: token.colorTextTertiary, fontSize: 12 }}>
            <Spin size="small" /> &nbsp;thinking…
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6, padding: 8, borderTop: `1px solid ${token.colorBorderSecondary}` }}>
        <Input.TextArea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Type a message…"
          autoSize={{ minRows: 1, maxRows: 3 }}
        />
        <Button variant="primary" icon={<Send size={16} />} onClick={send} loading={loading} aria-label="Send" />
      </div>
    </Card>
  );
}
